/**
 * Timeline Slider
 *
 * Splide (bundled with Bricks as `bricks-splide`) runs the slides. The element
 * root is the Splide root, so Splide finds the arrows / toggle wherever they
 * are rendered inside it. The timeline below is built here from the slides'
 * data-tls-* attributes, so it stays in sync when a single slide is
 * re-rendered in the builder.
 */

var IS_BUILDER = window.location.href.includes("brickspreview");

// iOS / iPadOS (every browser there is WebKit; iPadOS reports as a Mac): it
// partly reverts JS scroll writes between frames, so per-frame pinning jitters.
var IS_IOS = /iP(hone|ad|od)/.test(navigator.userAgent) || (/Macintosh/.test(navigator.userAgent) && navigator.maxTouchPoints > 1);

// Set once any slider on the page saw the same reverts elsewhere.
var scrollRevertSeen = false;

function parseJson(raw) {
    if (!raw) return {};
    try {
        return JSON.parse(raw) || {};
    } catch (e) {
        return {};
    }
}

function isNumber(value) {
    return typeof value === "number" && isFinite(value);
}

function clamp01(value) {
    return Math.min(1, Math.max(0, value));
}

function builderWarn(message) {
    if (IS_BUILDER) console.warn("[Timeline Slider] " + message);
}

class MadTimelineSlider {
    constructor(root) {
        if (root._madTls) root._madTls.destroy();

        this.root = root;
        this.list = root.querySelector(":scope > .mad-timeline-slider__stage > .splide__track > .splide__list");
        this.track = this.list ? this.list.parentElement : null;
        this.viewport = root.querySelector(":scope > .mad-timeline-slider__timeline > .mad-timeline-slider__timeline-viewport");
        this.scale = this.viewport ? this.viewport.querySelector(".mad-timeline-slider__scale") : null;
        this.timelineTrack = this.scale ? this.scale.parentElement : null;
        this.pointsList = this.scale ? this.scale.querySelector(".mad-timeline-slider__points") : null;

        if (!this.list || !this.scale || !this.pointsList) return;

        if (typeof window.Splide === "undefined") {
            console.warn("[Timeline Slider] Splide is not loaded (bricks-splide).");
            return;
        }

        this.options = parseJson(root.dataset.tlsOptions);
        this.config = Object.assign(
            { spacingMode: "proportional", rangeStart: null, rangeEnd: null, minPointDistance: 48, goToLabel: "Go to %s", adaptiveHeightMedia: null, adaptiveScroll: "", adaptiveScrollMethod: "auto" },
            parseJson(root.dataset.tlsConfig)
        );

        // Adaptive height: null = off, "" = every width, else a media query
        // ("from breakpoint X down") that is followed live.
        var adaptiveMedia = this.config.adaptiveHeightMedia;
        this.adaptiveEnabled = typeof adaptiveMedia === "string";
        this.adaptiveQuery = this.adaptiveEnabled && adaptiveMedia ? window.matchMedia(adaptiveMedia) : null;
        this.adaptiveHeight = false;

        // Scroll on height change: "keep" | "top" | "bottom" | "". Only for moves
        // the visitor started (pointer / keyboard on the slider), never autoplay.
        this.scrollMode = this.adaptiveEnabled ? this.config.adaptiveScroll || "" : "";
        this.timelineEl = root.querySelector(":scope > .mad-timeline-slider__timeline");
        this.intentAt = -Infinity;
        this.pinFrame = null;
        this.pendingPin = null;
        this.settleTimer = null;
        this.keepTarget = null;
        if (this.scrollMode) {
            this.onIntent = () => { this.intentAt = performance.now(); };
            ["pointerdown", "pointerup", "keydown"].forEach((type) => root.addEventListener(type, this.onIntent, true));

            // The visitor scrolling the page takes over. Touches on the slider
            // itself (arrows, dots, swipes) come right before the next move and
            // only count once they turn into a vertical page scroll.
            this.onScrollIntent = (e) => {
                if (e.type === "touchstart") {
                    var touch = e.touches[0];
                    this.touchStart = this.root.contains(e.target) && touch ? { x: touch.clientX, y: touch.clientY } : null;
                    if (this.touchStart) return;
                } else if (e.type === "touchmove") {
                    var move = e.touches[0];
                    if (!this.touchStart || !move) return;
                    var dx = Math.abs(move.clientX - this.touchStart.x);
                    var dy = Math.abs(move.clientY - this.touchStart.y);
                    if (dy < 10 || dy < dx) return;
                    this.touchStart = null;
                }
                this.keepTarget = null;
                this.stopPin();
            };
            ["wheel", "touchstart", "touchmove"].forEach((type) => window.addEventListener(type, this.onScrollIntent, { passive: true }));
        }

        this.index = 0;
        this.positions = [];
        this.points = [];
        this.lastWidth = -1;
        this.reinitTimer = null;

        root._madTls = this;

        // Read the slides before Splide mounts (and adds loop clones).
        this.items = this.collectSlides();
        this.buildPoints();
        this.layout();
        this.applyAdaptiveHeight();

        this.splide = new window.Splide(root, this.options);
        this.splide.on("mounted", () => {
            this.update(this.splide.index);
            this.scrollToActive(false);
            this.syncHeight();
        });
        this.splide.on("move", (newIndex) => {
            var pin = this.capturePin(); // positions before the height changes
            this.update(newIndex);
            this.scrollToActive(true);
            this.syncHeight();
            if (pin) {
                if (this.scrollMethod() === "settle") this.settlePin(pin);
                else this.runPin(pin);
            }
        });
        this.splide.on("resized", () => this.syncHeight());
        this.splide.mount();

        // Recompute min-distance positions on width changes only (mobile
        // address-bar resizes change only the height).
        this.resizeObserver = new ResizeObserver((entries) => {
            var width = entries[0].contentRect.width;
            if (width === this.lastWidth) return;
            this.lastWidth = width;
            this.layout();
            this.scrollToActive(false);
        });
        this.resizeObserver.observe(this.scale);

        // Web fonts change the label widths after the first layout.
        if (document.fonts && document.fonts.ready) {
            document.fonts.ready.then(() => this.root._madTls === this && this.fitLabels());
        }

        // Adaptive height: follow content that changes height later (images).
        // syncHeight() is a no-op while the breakpoint doesn't match.
        if (this.adaptiveEnabled) {
            this.heightObserver = new ResizeObserver(() => this.syncHeight());
            this.items.forEach((item) => this.heightObserver.observe(item.el));
        }
        if (this.adaptiveQuery) {
            this.onAdaptiveChange = () => this.applyAdaptiveHeight();
            this.adaptiveQuery.addEventListener("change", this.onAdaptiveChange);
        }

        // Builder: a slide edit re-renders only that slide's node, so rebuild
        // when the list's direct children change.
        if (IS_BUILDER) {
            this.mutationObserver = new MutationObserver((records) => {
                var relevant = records.some(function (record) {
                    return Array.from(record.addedNodes).concat(Array.from(record.removedNodes)).some(function (node) {
                        // Splide adds/removes loop clones itself — ignore those.
                        return node.nodeType === 1 && !node.classList.contains("splide__slide--clone");
                    });
                });
                if (relevant) this.scheduleReinit();
            });
            this.mutationObserver.observe(this.list, { childList: true });
        }
    }

    collectSlides() {
        var children = Array.from(this.list.children).filter(function (el) {
            return !el.classList.contains("splide__slide--clone");
        });

        return children.map(function (el, i) {
            // Anything dropped straight into the list still becomes a slide.
            if (!el.classList.contains("splide__slide")) el.classList.add("splide__slide");

            var raw = el.dataset.tlsValue;
            var value = raw !== undefined && raw !== "" && isFinite(raw) ? parseFloat(raw) : null;

            return {
                el: el,
                value: value,
                label: el.dataset.tlsLabel || (value !== null ? raw : String(i + 1)),
                caption: el.dataset.tlsCaption || "",
            };
        });
    }

    buildPoints() {
        var goTo = String(this.config.goToLabel || "%s");

        this.pointsList.textContent = "";
        this.points = this.items.map((item, i) => {
            var point = document.createElement("li");
            point.className = "mad-timeline-slider__point";

            var button = document.createElement("button");
            button.type = "button";
            button.className = "mad-timeline-slider__point-button";
            button.setAttribute("aria-label", goTo.replace("%s", item.caption ? item.label + ", " + item.caption : item.label));
            button.addEventListener("click", () => this.splide && this.splide.go(i));

            var dot = document.createElement("span");
            dot.className = "mad-timeline-slider__dot";

            var labelWrap = document.createElement("span");
            labelWrap.className = "mad-timeline-slider__label-wrap";

            var label = document.createElement("span");
            label.className = "mad-timeline-slider__label";
            label.textContent = item.label;
            labelWrap.appendChild(label);

            if (item.caption) {
                var caption = document.createElement("span");
                caption.className = "mad-timeline-slider__caption";
                caption.textContent = item.caption;
                labelWrap.appendChild(caption);
            }

            button.appendChild(dot);
            button.appendChild(labelWrap);
            point.appendChild(button);
            this.pointsList.appendChild(point);

            return point;
        });

        var values = this.items.map(function (item) { return item.value; });
        var ascending = values.every(function (v, i) { return i === 0 || v === null || values[i - 1] === null || v >= values[i - 1]; });
        if (!ascending) builderWarn("Slide values are not in ascending order — progress and past states follow the slide order.");
    }

    /**
     * Point positions as fractions 0..1 of the scale width.
     */
    computePositions(width) {
        var n = this.items.length;
        if (!n) return [];
        if (n === 1) return [0.5];

        var equal = this.items.map(function (_, i) { return i / (n - 1); });
        var positions = equal;

        if (this.config.spacingMode === "proportional") {
            var values = this.items.map(function (item) { return item.value; });

            if (values.some(function (v) { return v === null; })) {
                builderWarn("Not every slide has a numeric timeline value — using equal spacing.");
            } else {
                var min = isNumber(this.config.rangeStart) ? this.config.rangeStart : Math.min.apply(null, values);
                var max = isNumber(this.config.rangeEnd) ? this.config.rangeEnd : Math.max.apply(null, values);

                if (max > min) {
                    positions = values.map(function (v) { return clamp01((v - min) / (max - min)); });
                }
            }
        }

        // Enforce the minimum point distance, in value order.
        var gap = width > 0 ? Math.max(0, Number(this.config.minPointDistance) || 0) / width : 0;
        if (gap <= 0) return positions;

        if ((n - 1) * gap > 1) {
            builderWarn("The timeline is too narrow for the minimum point distance — using equal spacing.");
            return equal;
        }

        var order = positions.map(function (_, i) { return i; }).sort(function (a, b) {
            return positions[a] - positions[b] || a - b;
        });
        var sorted = order.map(function (i) { return positions[i]; });

        for (var k = 1; k < n; k++) sorted[k] = Math.max(sorted[k], sorted[k - 1] + gap);
        sorted[n - 1] = Math.min(sorted[n - 1], 1);
        for (var j = n - 2; j >= 0; j--) sorted[j] = Math.min(sorted[j], sorted[j + 1] - gap);

        var result = positions.slice();
        order.forEach(function (i, k) { result[i] = clamp01(sorted[k]); });
        return result;
    }

    layout() {
        this.positions = this.computePositions(this.scale.clientWidth);
        this.points.forEach((point, i) => {
            point.style.setProperty("--tls-pos", this.positions[i] * 100 + "%");
        });
        this.update(this.index);
    }

    update(index) {
        if (!(index >= 0) || index >= this.points.length) index = 0;
        this.index = index;

        var active = this.positions[index] || 0;

        this.points.forEach((point, i) => {
            var isActive = i === index;
            point.classList.toggle("is-active", isActive);
            // Strictly left of the active point; the active point is never also "past".
            point.classList.toggle("is-past", !isActive && this.positions[i] < active);

            var button = point.firstChild;
            if (isActive) button.setAttribute("aria-current", "step");
            else button.removeAttribute("aria-current");
        });

        this.scale.style.setProperty("--tls-progress", active * 100 + "%");

        // Active typography can change a label's width.
        this.fitLabels();
    }

    /**
     * Labels are centred under their point, so at the first / last point half
     * of the label hangs past the edge: on the right that widens the viewport
     * (scrollbar), on the left it gets clipped. Shift such labels inward just
     * enough to stay inside the timeline track (incl. its inner padding).
     * Reset all → read all → write all, so this costs one layout.
     */
    fitLabels() {
        if (!this.timelineTrack) return;

        var wraps = this.points.map(function (point) {
            return point.querySelector(".mad-timeline-slider__label-wrap");
        });
        wraps.forEach(function (wrap) {
            if (wrap) wrap.style.removeProperty("--tls-label-shift");
        });

        var bounds = this.timelineTrack.getBoundingClientRect();
        var shifts = wraps.map(function (wrap) {
            if (!wrap) return 0;
            var rect = wrap.getBoundingClientRect();
            if (!rect.width) return 0; // hidden label
            if (rect.width >= bounds.width || rect.left < bounds.left) return bounds.left - rect.left;
            if (rect.right > bounds.right) return bounds.right - rect.right;
            return 0;
        });

        wraps.forEach(function (wrap, i) {
            if (wrap && shifts[i]) wrap.style.setProperty("--tls-label-shift", shifts[i] + "px");
        });
    }

    /**
     * Centre the active point in a horizontally scrolling timeline. Uses
     * scrollTo on the viewport — scrollIntoView would also scroll the page.
     */
    scrollToActive(smooth) {
        var viewport = this.viewport;
        var point = this.points[this.index];
        if (!point || viewport.scrollWidth <= viewport.clientWidth + 1) return;

        var left = viewport.scrollLeft + (point.getBoundingClientRect().left - viewport.getBoundingClientRect().left) - viewport.clientWidth / 2;
        viewport.scrollTo({ left: left, behavior: smooth ? "smooth" : "auto" });
    }

    syncHeight() {
        if (!this.adaptiveHeight || !this.splide) return;
        var item = this.items[this.index];
        if (item) this.track.style.height = item.el.offsetHeight + "px";
    }

    /**
     * Switch adaptive height on / off for the current width: the modifier class
     * carries the CSS (height transition, slides aligned to the top), the
     * inline track height is only set while it is on.
     */
    applyAdaptiveHeight() {
        var on = this.adaptiveEnabled && (!this.adaptiveQuery || this.adaptiveQuery.matches);
        this.adaptiveHeight = on;
        this.root.classList.toggle("mad-timeline-slider--adaptive-height", on);
        if (on) this.syncHeight();
        else if (this.track) this.track.style.height = "";
    }

    /**
     * "frame" or "settle". Auto: settle on iOS and once reverts were seen,
     * frame everywhere else.
     */
    scrollMethod() {
        if (!this.isAutoMethod()) return this.config.adaptiveScrollMethod;
        return IS_IOS || scrollRevertSeen ? "settle" : "frame";
    }

    isAutoMethod() {
        var method = this.config.adaptiveScrollMethod;
        return method !== "frame" && method !== "settle";
    }

    /**
     * Where the page should be after a height change, measured before it
     * starts. Returns null when nothing should scroll.
     */
    capturePin() {
        if (!this.scrollMode || !this.adaptiveHeight) return null;
        // Visitor-started moves only: a pointer / key event on the slider shortly before.
        if (performance.now() - this.intentAt > 1500) return null;

        var mode = this.scrollMode;
        var anchor = mode === "keep" ? this.timelineEl : this.root;
        if (!anchor) return null;

        var rect = anchor.getBoundingClientRect();
        var style = getComputedStyle(this.root);

        if (mode === "keep") return { anchor: anchor, edge: "top", start: rect.top, target: this.keepTargetFor(rect.top) };
        if (mode === "top") return { anchor: anchor, edge: "top", start: rect.top, target: parseFloat(style.scrollMarginTop) || 0 };
        return { anchor: anchor, edge: "bottom", start: rect.bottom, target: window.innerHeight - (parseFloat(style.scrollMarginBottom) || 0) };
    }

    /**
     * "keep" target. In settle mode the timeline is pushed away until the
     * smooth scroll brings it back, so a move during the wait or the glide
     * keeps the original spot instead of the displaced one. Dropped when it
     * expires or the visitor scrolls the page.
     */
    keepTargetFor(top) {
        if (this.scrollMethod() !== "settle") return top;

        var now = performance.now();
        if (!this.keepTarget || now > this.keepTarget.until) this.keepTarget = { value: top, until: 0 };
        this.keepTarget.until = now + this.heightDuration() + 1500;
        return this.keepTarget.value;
    }

    /**
     * Scroll the page frame by frame while the track height animates, so the
     * anchor edge moves from its start position to its target (eased) instead
     * of being pushed around by the height change. "keep": start = target.
     */
    runPin(pin) {
        this.stopPin();

        var duration = this.heightDuration();
        var startTime = performance.now();
        var html = document.documentElement;
        var track = this.track;
        var ease = function (t) { return t < 0.5 ? 2 * t * t : 1 - Math.pow(-2 * t + 2, 2) / 2; };

        // A page-wide `scroll-behavior: smooth` would turn each correction into
        // its own smooth scroll; switch it off while pinning.
        this.pinScrollBehavior = html.style.scrollBehavior;
        this.pinScrollBehaviorSet = true;
        html.style.scrollBehavior = "auto";

        // Auto: watch for scroll changes between frames that this loop didn't
        // make (the browser reverting its writes). Seen twice → finish this
        // run, and every later one on the page, as settle.
        var watch = this.isAutoMethod();
        var lastScroll = window.scrollY;
        var reverts = 0;

        var step = (now) => {
            if (watch && Math.abs(window.scrollY - lastScroll) > 2 && ++reverts >= 2) {
                scrollRevertSeen = true;
                this.stopPin();
                // A move during the glide keeps the original spot (see keepTargetFor).
                if (this.scrollMode === "keep") this.keepTarget = { value: pin.target, until: performance.now() + this.heightDuration() + 1500 };
                this.settlePin(pin);
                return;
            }

            var elapsed = now - startTime;
            var progress = duration > 0 ? Math.min(1, elapsed / duration) : 1;
            var desired = pin.start + (pin.target - pin.start) * ease(progress);
            var delta = pin.anchor.getBoundingClientRect()[pin.edge] - desired;
            if (Math.abs(delta) >= 0.5) window.scrollBy(0, delta);
            lastScroll = window.scrollY;

            // The CSS height transition starts a frame later than this loop, so
            // keep going until the track has reached its height (capped).
            var settled = !track.style.height || Math.abs(track.getBoundingClientRect().height - parseFloat(track.style.height)) < 0.5;
            if ((progress < 1 || !settled) && elapsed < duration + 500) {
                this.pinFrame = requestAnimationFrame(step);
            } else {
                this.stopPin();
            }
        };
        this.pinFrame = requestAnimationFrame(step);
    }

    /**
     * Leave the page alone while the track height animates, then scroll once
     * (smoothly) so the anchor edge lands on its target. For browsers that
     * can't keep per-frame scrolling in step with the layout (iOS WebKit).
     */
    settlePin(pin) {
        this.stopPin();
        this.pendingPin = pin;

        var finish = () => this.finishSettle();
        // transitionend bubbles up from the slides' own transitions.
        this.onSettleEnd = (e) => {
            if (e.target === this.track && e.propertyName === "height") finish();
        };
        this.track.addEventListener("transitionend", this.onSettleEnd);
        // No transitionend when the height doesn't change.
        this.settleTimer = setTimeout(finish, this.heightDuration() + 100);
    }

    finishSettle() {
        var pin = this.pendingPin;
        this.clearSettle();
        if (!pin) return;

        var scroller = document.scrollingElement || document.documentElement;
        var max = Math.max(0, scroller.scrollHeight - window.innerHeight);
        // Clamped: iOS reports wrong rects after scrolling past the end.
        var top = Math.min(max, Math.max(0, window.scrollY + pin.anchor.getBoundingClientRect()[pin.edge] - pin.target));
        if (Math.abs(top - window.scrollY) >= 1) window.scrollTo({ top: top, behavior: "smooth" });
    }

    clearSettle() {
        clearTimeout(this.settleTimer);
        this.settleTimer = null;
        if (this.onSettleEnd) {
            this.track.removeEventListener("transitionend", this.onSettleEnd);
            this.onSettleEnd = null;
        }
        this.pendingPin = null;
    }

    stopPin() {
        if (this.pinFrame !== null) cancelAnimationFrame(this.pinFrame);
        this.pinFrame = null;
        if (this.pinScrollBehaviorSet) {
            document.documentElement.style.scrollBehavior = this.pinScrollBehavior || "";
            this.pinScrollBehaviorSet = false;
        }
        this.clearSettle();
    }

    /**
     * Duration of the track's height transition in ms (longest listed).
     */
    heightDuration() {
        if (!this.track) return 0;
        return getComputedStyle(this.track).transitionDuration.split(",").reduce(function (max, value) {
            var ms = parseFloat(value) * (value.trim().endsWith("ms") ? 1 : 1000);
            return isFinite(ms) ? Math.max(max, ms) : max;
        }, 0);
    }

    scheduleReinit() {
        clearTimeout(this.reinitTimer);
        this.reinitTimer = setTimeout(() => {
            if (this.root.isConnected) new MadTimelineSlider(this.root);
        }, 100);
    }

    destroy() {
        clearTimeout(this.reinitTimer);
        if (this.mutationObserver) this.mutationObserver.disconnect();
        if (this.resizeObserver) this.resizeObserver.disconnect();
        if (this.heightObserver) this.heightObserver.disconnect();
        if (this.adaptiveQuery && this.onAdaptiveChange) this.adaptiveQuery.removeEventListener("change", this.onAdaptiveChange);
        if (this.onIntent) {
            ["pointerdown", "pointerup", "keydown"].forEach((type) => this.root.removeEventListener(type, this.onIntent, true));
        }
        if (this.onScrollIntent) {
            ["wheel", "touchstart", "touchmove"].forEach((type) => window.removeEventListener(type, this.onScrollIntent));
        }
        this.stopPin();
        this.keepTarget = null;
        if (this.splide) this.splide.destroy(true);
        if (this.pointsList) this.pointsList.textContent = "";
        if (this.scale) this.scale.style.removeProperty("--tls-progress");
        if (this.track) this.track.style.height = "";
        if (this.root._madTls === this) delete this.root._madTls;
    }
}

function setupMadTimelineSlider() {
    document.querySelectorAll(".mad-timeline-slider").forEach(function (root) {
        // Frontend: init once. Builder: always rebuild (Bricks calls this after a re-render).
        if (root._madTls && !IS_BUILDER) return;
        try {
            new MadTimelineSlider(root);
        } catch (e) {
            console.error("[Timeline Slider]", e);
        }
    });
}

window.setupMadTimelineSlider = setupMadTimelineSlider;

if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", setupMadTimelineSlider);
} else {
    setupMadTimelineSlider();
}
